/**
 * Loads Omicall WebSDK once per session and registers the current user's extension.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Stack, Typography } from '@/shared/ui';
import { queryKeys } from '@/shared/query';
import { usePermission } from '@/shared/hooks/usePermission';
import { useSnackbar } from '@/shared/hooks/useSnackbar';
import { callsAPI, type ContactLog } from './api/calls.api';
import { loadOmicallSdk } from './omicallSdk';
import IncomingCallDialog from './IncomingCallDialog';
import { setIncomingCallHandler } from './incomingCallBus';

function useMySipCredentials() {
  const { hasPermission } = usePermission();
  return useQuery({
    queryKey: queryKeys.calls.meSip,
    queryFn: async () => {
      const res = await callsAPI.getMySip();
      return res.data ?? null;
    },
    enabled: hasPermission('calls:use'),
    retry: false,
  });
}

/** True when this user has an assigned extension the softphone can register. */
export function useOmicallExtensionReady(): boolean {
  return Boolean(useMySipCredentials().data);
}

export function useOmicallMakeCall(): (phone: string, name?: string) => Promise<void> {
  const { t } = useTranslation('calls');
  const { showSnackbar } = useSnackbar();
  const sipQuery = useMySipCredentials();
  return useCallback(
    async (phone: string, name?: string) => {
      if (!sipQuery.data) {
        showSnackbar(t('errors.notConfigured'), 'warning');
        return;
      }
      try {
        const sdk = await loadOmicallSdk();
        sdk.makeCall(phone, name ? { remoteContact: { name } } : undefined);
      } catch {
        showSnackbar(t('errors.makeCall'), 'error');
      }
    },
    [showSnackbar, sipQuery.data, t]
  );
}

const CallSoftphoneHost: React.FC = () => {
  const { t } = useTranslation('calls');
  const { showSnackbar } = useSnackbar();
  const { hasPermission } = usePermission();
  const canUse = hasPermission('calls:use');
  const [status, setStatus] = useState<'idle' | 'online' | 'busy' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [incoming, setIncoming] = useState<ContactLog | null>(null);
  const initRef = useRef(false);

  const sipQuery = useMySipCredentials();

  useEffect(() => {
    if (!canUse) {
      setIncomingCallHandler(null);
      return undefined;
    }
    setIncomingCallHandler((contactId) => {
      const sipUser = sipQuery.data?.sipUser;
      if (!sipUser) return;
      void callsAPI.getContact(contactId).then((res) => {
        const log = res.data;
        if (!log?.call || log.call.status !== 'ringing' || log.call.direction !== 'inbound') return;
        if (log.call.sipUser && log.call.sipUser !== sipUser) return;
        setIncoming(log);
      });
    });
    return () => setIncomingCallHandler(null);
  }, [canUse, sipQuery.data]);

  useEffect(() => {
    if (!canUse || !sipQuery.data || initRef.current) return;
    if (
      typeof window !== 'undefined' &&
      window.location.protocol !== 'https:' &&
      window.location.hostname !== 'localhost' &&
      window.location.hostname !== '127.0.0.1'
    ) {
      setStatus('error');
      setError(t('status.httpsRequired'));
      return;
    }
    const creds = sipQuery.data;
    initRef.current = true;
    void (async () => {
      try {
        const sdk = await loadOmicallSdk();
        const ok = await sdk.init({
          lng: 'vn',
          ui: { toggleDial: 'show', dialPosition: 'right' },
          searchRemoteContact: async (data) => {
            const phone = data.phone || data.number || '';
            if (!phone) return { name: '' };
            try {
              const res = await callsAPI.lookup(phone);
              return { name: res.data?.name || '' };
            } catch {
              return { name: '' };
            }
          },
        });
        if (!ok) {
          setStatus('error');
          setError(t('errors.register'));
          return;
        }
        const registered = await sdk.register({
          sipRealm: creds.sipRealm,
          sipUser: creds.sipUser,
          sipPassword: creds.sipPassword,
        });
        if (registered && registered.status === false) {
          setStatus('error');
          setError(t('errors.register'));
          return;
        }
        setStatus('online');
        setError(null);
      } catch (err) {
        setStatus('error');
        const message = err instanceof Error ? err.message : t('errors.register');
        setError(message.includes('NotAllowedError') || message.includes('Permission') ? t('status.micDenied') : t('errors.register'));
      }
    })();
  }, [canUse, sipQuery.data, t]);

  const setBusy = async (busy: boolean) => {
    try {
      const sdk = await loadOmicallSdk();
      if (busy && sdk.SB_STATE?.BUSY !== undefined) {
        sdk.reregister(sdk.SB_STATE.BUSY);
        setStatus('busy');
      } else if (sipQuery.data) {
        await sdk.register({
          sipRealm: sipQuery.data.sipRealm,
          sipUser: sipQuery.data.sipUser,
          sipPassword: sipQuery.data.sipPassword,
        });
        setStatus('online');
      }
    } catch {
      showSnackbar(t('errors.register'), 'error');
    }
  };

  if (!canUse) return <IncomingCallDialog incoming={incoming} onClose={() => setIncoming(null)} />;

  return (
    <>
      {status === 'error' && error ? (
        <Box sx={{ position: 'fixed', bottom: 16, left: 16, zIndex: 1300, maxWidth: 360 }}>
          <Alert severity="warning">{error}</Alert>
        </Box>
      ) : null}
      {sipQuery.data && status !== 'error' ? (
        <Box sx={{ position: 'fixed', bottom: 16, left: 88, zIndex: 1200 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" color="text.secondary">
              {status === 'busy' ? t('status.busy') : t('status.online')}
            </Typography>
            {status === 'online' ? (
              <Button size="small" onClick={() => void setBusy(true)}>
                {t('actions.busy')}
              </Button>
            ) : status === 'busy' ? (
              <Button size="small" onClick={() => void setBusy(false)}>
                {t('actions.ready')}
              </Button>
            ) : null}
          </Stack>
        </Box>
      ) : null}
      <IncomingCallDialog incoming={incoming} onClose={() => setIncoming(null)} />
    </>
  );
};

export default CallSoftphoneHost;
